import { Pencil1Icon, TrashIcon, EyeOpenIcon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { ConfirmAlertDialog } from "../../../components/common/ConfirmAlertDialog";
import { Lists } from "../../../components/common/Lists";
import TableComponent from "../../../components/common/Tables";
import { useAppDispatch } from "../../../hooks/hooks";
import { useGetProjectsQuery } from "../../../hooks/scientific/useGetProjectsQuery";
import { routes } from "../../../routes";
import { ProjectType } from "../../../types/ScientificModule";
import { Key_projects } from "../../../utils/constants";
import { updateProject } from "../../../services/scientific/project";
import { setProjectsData } from "../../../redux/slices/scientific/projects";
import { getPhases } from "../../../services/scientific/phase";
import { Badge } from "@radix-ui/themes";
import { useSelector } from "react-redux";
import { useGetJudgeExhibitorByUserIdQuery } from "../../../hooks/scientific/useGetJudgeExhibitorByUserIdQuery";
import { RootState } from "../../../redux/store";
import { getProjectsDocuments } from "../../../services/scientific/projectDocument";

const columnNamesMap: { [key: string]: string } = {
  description: 'Descrição',
  currentPhase: 'Fase actual',
  startdate: 'Data de Início',
  icon: "Terminado?"
};

export const Projects = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const userLogged = useSelector((state: RootState) => state.login.userData);
  const { data: judgeOrExhibitor } = useGetJudgeExhibitorByUserIdQuery(userLogged.UserId, {
    enabled: !!userLogged.UserId
  })

  const { data, status } = useGetProjectsQuery({ PageNumber: currentPage, PageSize: 400 });
  const [deleteItem, setDeleteItem] = useState<ProjectType | null>(null);
  const [startProjectitem, setStartProjectitem] = useState<ProjectType | null>(null);


  const mutationUpdate = useMutation(updateProject, {
    onSuccess: () => {
      queryClient.invalidateQueries(Key_projects);
      toast.success('Projeto atualizado com sucesso');
    },
    onError: () => {
      toast.error("Erro ao atualizar o Projeto.");
    }
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleClickCreate = () => {
    navigate(routes.createProject);
  };

  const handleEdit = async (c: ProjectType) => {
    try {
      
      c.icon = '';
      let { items } = await getProjectsDocuments({ PageSize: 400, PageNumber: 1, IdProject: c.id })
      items = items.filter(i => i.idProject === c.id);
      c.documents = items;
      dispatch(setProjectsData(c));
      navigate(routes.updateProject);
    }
    catch (ex) {
      console.error(ex);
    }
  };
  const handleStartProject = async (c: ProjectType) => {
    try {
      c.icon = '';
      const { items } = await getPhases({ PageSize: 400, PageNumber: 1, IdProjectType: c.idProjectType })
      const startItem = items.filter(i => i.idProjectType === c.idProjectType).find(i => i.start);
      c.idCurrentPhase = startItem?.id;
      await mutationUpdate.mutateAsync(c);
    }
    catch (e) {
      toast.error("Erro ao iniciar o Projeto.");
    }

  };

  const handleViewProject = async (c: ProjectType) => {

    try {
      let { items } = await getProjectsDocuments({ PageSize: 400, PageNumber: 1, IdProject: c.id })
      items = items.filter(i => i.idProject === c.id);
      c.documents = items;

      dispatch(setProjectsData(c));
      navigate(routes.viewProjectSteps);
    }
    catch (ex) {
      console.error(ex);
    }
  };

  const handleAssignJudges = (c: ProjectType) => {
    c.icon = '';
    dispatch(setProjectsData(c));
    navigate(routes.createJudgePhaseProject);
  };

  const handleDelete = (c: ProjectType) => {
    c.status = false;
    mutationUpdate.mutateAsync(c);
  };

  const openDeleteDialog = (s: ProjectType) => {
    setDeleteItem(s);
  };
  const openStartProjectDialog = (s: ProjectType) => {
    setStartProjectitem(s);
  };

  const confirmDelete = async () => {
    if (deleteItem) {
      await handleDelete(deleteItem);
    }
  };
  const confirmStartProject = async () => {
    if (startProjectitem) {
      await handleStartProject(startProjectitem);
    }
  };

  const options = data?.items.map((c: ProjectType) => [
    {
      label: 'Editar',
      onClick: () => handleEdit(c),
      icon: <Pencil1Icon />
    },
    // {
    //   label: 'Initiar projecto',
    //   onClick: () => openStartProjectDialog(c),
    //   icon: <Pencil1Icon />
    // },
    {
      label: 'Visualizar',
      onClick: () => handleViewProject(c),
      icon: <EyeOpenIcon />
    },
    {
      label: 'Atribuir Validadores',
      onClick: () => handleAssignJudges(c),
      icon: <Pencil1Icon />
    },
    {
      label: 'Excluir',
      onClick: () => openDeleteDialog(c),
      icon: <TrashIcon />
    }
  ]) || [];

  useEffect(() => {
    if (judgeOrExhibitor?.idExhibitor) {
      navigate(routes.myProjectsAsExbitors)
    }
  }, [])

  return (
    <>
      <Lists
        status={status}
        TableComponent={() =>
          <TableComponent
            columns={Object.keys(columnNamesMap)}
            items={data?.items.map(i => {
              return {
                ...i,
                icon: i.isEnded ? <Badge color="green"> Terminado</Badge> : <Badge color="gray"> Não Terminado</Badge>
              }
            })}
            options={options}
            columnNamesMap={columnNamesMap}
            totalPages={data?.totalPages || 1}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        }
        handleClickCreate={handleClickCreate}
      />
      <ConfirmAlertDialog
        open={!!deleteItem}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        title="Esta operação é irreversível"
        item={deleteItem}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteItem(null)}
      />
      <ConfirmAlertDialog
        open={!!startProjectitem}
        onOpenChange={(open) => !open && setStartProjectitem(null)}
        title="Esta operação é irreversível"
        item={startProjectitem}
        onConfirm={confirmStartProject}
        onCancel={() => setStartProjectitem(null)}
      />
    </>
  );
};
